// Libraries
import React from 'react'
import { ModuleNames } from '../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../constants/localization.constants'
import Layout from '../../layouts/layout'

// Thank You Page Modules
import ContentBlock from '../../global/contentBlock'
import FeaturedResource from '../../global/featuredResource'
import Hero from '../../global/hero'
import DynamicRow from '../../global/relatedResources'
import ResourceRow from '../../global/resourceRow'
import SEOTags from '../../global/seoTags'
import WrapperImage from '../../global/wrapperImage'

// Helpers
import ImgUtil from '../../../utils/img.utils'
import { findTarget } from '../../../utils/microcopy.utils'
import { renderRichText } from '../../../utils/richTextHandler.utils'
import formatInlineCode from '../../../utils/shortTextFormatting'
import Typography from '../../global/typography'

// Styles
import { ResourceRowContainer } from '../../global/relatedResources/style'
import { TimezoneOverrideContainer } from './style'

const ThankYouPage = ({
  path,
  pageContext,
  location,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}: any) => {
  if (pageContext && pageContext.thankYouPageData) {
    let dynamicPageContent: any,
      hero: any,
      thankYouPageHero: any,
      pageData: any,
      pageType: string = '',
      generics: { key: string; value: any }[] = [],
      resourseRowIntro: string,
      dynamicRow: any,
      displayDynamicRow: boolean = true,
      thankYouFeaturedResource = pageContext.thankYouFeaturedResource || null

    pageData = pageContext.thankYouPageData
    pageType = 'thankYou'

    let defaultLocale: string
    if (pageContext && pageContext.pageLocale) {
      defaultLocale = pageContext.pageLocale
    } else {
      defaultLocale = LocaleCodes.ENGLISH.code
    }

    const pageTitle = pageData.name ? pageData.name : ''
    const resourceNumber = pageData?.resourceCategory?.number || 8 // default to 8
    generics = pageData?.genericIntro?.values || []
    let downloadAssetExists

    if (pageData?.referrer?.downloadAsset?.id || pageData?.downloadPdf?.id) {
      downloadAssetExists = true
    } else {
      downloadAssetExists = false
    }

    resourseRowIntro =
      pageData?.resourceIntro ||
      findTarget(`resource.${resourceNumber}.ThankYouROWINTRO.text`, generics)
    const relatedResources = pageContext?.relatedResources || []

    dynamicRow = (
      <DynamicRow
        resourseRowIntro={resourseRowIntro}
        featuredResource={thankYouFeaturedResource}
        relatedResources={relatedResources}
        location={location}
        locale={defaultLocale}
      />
    )

    thankYouPageHero = {
      headline:
        pageData.title || findTarget(`resource.${resourceNumber}.ThankYouTITLE.text`, generics),
      subheadline: {
        subheadline:
          pageData.subtitle ||
          findTarget(
            downloadAssetExists
              ? `resource.${resourceNumber}.ThankYouDOWNLOAD.text`
              : `resource.${resourceNumber}.ThankYouSUBTITLE.text`,
            generics
          ),
      },
      cta: pageData.downloadPdf ? [pageData.downloadPdf] : '',
      useCase: 'Centered text with dots',
      heroTheme: 'light',
    }

    hero = (
      <Hero
        data={thankYouPageHero}
        location={location}
        pageTitle={pageTitle}
        showBreadcrumbs={true}
        pageType={pageType}
        locale={defaultLocale}
      />
    )

    if (pageData && (pageData.flexSection || pageData.modules)) {
      const thankYouPageContent = pageData.flexSection ? pageData.flexSection : pageData.modules
      dynamicPageContent = thankYouPageContent.map((module: any, i: Number) => {
        switch (module.__typename) {
          case ModuleNames.GLOBAL_CONTENT_BLOCK:
            return <ContentBlock data={module} key={`page-component--${i}`}></ContentBlock>

          case ModuleNames.RESOURCE_ROW:
            // disable the dynamic row if the module is present
            displayDynamicRow = false
            return (
              <ResourceRowContainer>
                <Typography className="ResourseRow_Intro" type="header2">
                  {formatInlineCode(
                    resourseRowIntro ||
                      findTarget(`resource.${resourceNumber}.ThankYouROWINTRO.text`, generics)
                  )}
                </Typography>
                <ResourceRow
                  data={module}
                  key={`page-component--${i}`}
                  locale={defaultLocale}></ResourceRow>
              </ResourceRowContainer>
            )

          case ModuleNames.FEATURED_RESOURCE:
            return (
              <FeaturedResource
                data={module}
                pageLocale={defaultLocale}
                key={`page-component--${i}`}></FeaturedResource>
            )
        }
      })
    }

    const defaultSlug = pageData && pageData.slug ? pageData.slug : '/'
    const seoData = pageData && pageData.seoMetadata ? pageData.seoMetadata : null

    const timezoneOverride =
      pageData && pageData.timezoneOverride ? pageData.timezoneOverride : null

    const isUpcoming = pageData?.referrer?.useCase === 'Upcoming'

    const siteSection = 'Product'
    const pageInner = (
      <div data-cy={`ThankYouPage`}>
        <SEOTags
          seoMetadata={seoData}
          slug={defaultSlug}
          path={path}
          locale={defaultLocale}
          siteSection={siteSection}
          availableLocales={pageContext.availableLocales}
        />
        {isUpcoming && timezoneOverride ? (
          <TimezoneOverrideContainer>
            {timezoneOverride?.icon?.colorIcon?.file?.url && (
              <WrapperImage
                src={ImgUtil.getResizedImageUrl(timezoneOverride.icon.colorIcon.file.url, '')}
                alt={
                  timezoneOverride?.icon?.colorIcon?.seo
                    ? timezoneOverride.icon.colorIcon.seo
                    : 'UiPath Icon'
                }
                loading="lazy"
                className="TimeZoneOverried__Icon"
                threshold={150}
              />
            )}
            {timezoneOverride.body && renderRichText(timezoneOverride.body)}
          </TimezoneOverrideContainer>
        ) : (
          <>
            {hero}
            {dynamicPageContent}
            {displayDynamicRow && dynamicRow}
          </>
        )}
      </div>
    )

    return (
      <Layout.Template
        lang={defaultLocale}
        headerData={headerData}
        footerData={footerData}
        announcementData={announcementData}
        mainNavBannerData={mainNavBannerData}
        browserLanguageData={browserLanguageData}
        pageType={pageType}
        slug={defaultSlug}
        availableLocales={pageContext.availableLocales}>
        {pageInner}
      </Layout.Template>
    )
  }
  return null
}

export default ThankYouPage
